import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const normalizeOptions = (input) => {
    if (typeof input === "string") {
        return { message: input };
    }
    return input || {};
};

const fallbackConfirm = (options) => Promise.resolve(window.confirm(options.message || options.title || "Confirmer cette action ?"));
const fallbackAlert = (options) => {
    window.alert(options.message || options.title || "Information");
    return Promise.resolve(true);
};

export const confirmAction = (input) => {
    const options = normalizeOptions(input);
    if (typeof window !== "undefined" && typeof window.somaConfirm === "function") {
        return window.somaConfirm(options);
    }
    return fallbackConfirm(options);
};

export const alertAction = (input) => {
    const options = normalizeOptions(input);
    if (typeof window !== "undefined" && typeof window.somaAlert === "function") {
        return window.somaAlert(options);
    }
    return fallbackAlert(options);
};

const intentConfig = {
    danger: { icon: "bx-trash", title: "Confirmer la suppression", confirmText: "Supprimer" },
    warning: { icon: "bx-error-circle", title: "Confirmation requise", confirmText: "Continuer" },
    success: { icon: "bx-check-shield", title: "Valider l'action", confirmText: "Valider" },
    info: { icon: "bx-info-circle", title: "Information", confirmText: "Compris" },
    primary: { icon: "bx-check-circle", title: "Confirmer l'action", confirmText: "Confirmer" },
};

const enrichOptions = (rawOptions = {}) => {
    const intent = rawOptions.intent || "primary";
    const config = intentConfig[intent] || intentConfig.primary;
    return {
        intent,
        title: rawOptions.title || config.title,
        message: rawOptions.message || "Souhaites-tu continuer ?",
        details: rawOptions.details || "",
        icon: rawOptions.icon || config.icon,
        confirmText: rawOptions.confirmText || config.confirmText,
        cancelText: rawOptions.cancelText || "Annuler",
        hideCancel: Boolean(rawOptions.hideCancel),
    };
};

const ConfirmProvider = ({ children }) => {
    const [dialog, setDialog] = useState(null);
    const dialogRef = useRef(null);

    const closeDialog = useCallback((result) => {
        setDialog((current) => {
            current?.resolve?.(result);
            return null;
        });
    }, []);

    const openDialog = useCallback((rawOptions, alertMode = false) => new Promise((resolve) => {
        setDialog({
            ...enrichOptions(rawOptions),
            hideCancel: alertMode ? true : Boolean(rawOptions?.hideCancel),
            resolve,
        });
    }), []);

    useEffect(() => {
        window.somaConfirm = (options) => openDialog(options, false);
        window.somaAlert = (options) => openDialog({ intent: "info", confirmText: "Compris", ...normalizeOptions(options) }, true);

        return () => {
            delete window.somaConfirm;
            delete window.somaAlert;
        };
    }, [openDialog]);

    useEffect(() => {
        if (!dialog) return undefined;

        const previousFocus = document.activeElement;
        dialogRef.current?.querySelector(".soma-confirm-secondary, .soma-confirm-primary")?.focus();
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeDialog(false);
            }
            if (event.key === "Tab") {
                const buttons = Array.from(dialogRef.current?.querySelectorAll("button:not(:disabled)") || []);
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first?.focus();
                }
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, [closeDialog, dialog]);

    const portalTarget = useMemo(() => (typeof document !== "undefined" ? document.body : null), []);

    return (
        <>
            {children}
            {dialog && portalTarget && createPortal(
                <div className="soma-confirm-backdrop" role="presentation" onMouseDown={() => closeDialog(false)}>
                    <section
                        ref={dialogRef}
                        className={`soma-confirm-dialog soma-confirm-${dialog.intent}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="soma-confirm-title"
                        aria-describedby="soma-confirm-message"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <button className="soma-confirm-close" type="button" aria-label="Fermer" onClick={() => closeDialog(false)}>
                            <i className="bx bx-x"></i>
                        </button>
                        <span className="soma-confirm-icon">
                            <i className={`bx ${dialog.icon}`}></i>
                        </span>
                        <div className="soma-confirm-copy">
                            <span className="soma-confirm-kicker">SOMA MASTER</span>
                            <h3 id="soma-confirm-title">{dialog.title}</h3>
                            <p id="soma-confirm-message">{dialog.message}</p>
                            {dialog.details && <small>{dialog.details}</small>}
                        </div>
                        <div className="soma-confirm-actions">
                            {!dialog.hideCancel && (
                                <button className="btn btn-light soma-confirm-secondary" type="button" onClick={() => closeDialog(false)}>
                                    {dialog.cancelText}
                                </button>
                            )}
                            <button className="btn btn-primary soma-confirm-primary" type="button" onClick={() => closeDialog(true)}>
                                {dialog.confirmText}
                            </button>
                        </div>
                    </section>
                </div>,
                portalTarget
            )}
        </>
    );
};

export default ConfirmProvider;
